import { getConfigObject } from '../util/config';
import clientLogger from '../util/logger';

const VERSION1 = '/api/v1';
const VERSION2 = '/api/v2';
const makeRequest = async (url, params) => {
  try {
    const response = await fetch(url, params);
    const result = await response.json();
    if (result.status >= 400) {
      const errorMessage = `Error ${result.status}: ${result.message} ${result.id}`;
      clientLogger(errorMessage);
      return {
        error: errorMessage,
      };
    }
    return result;
  } catch (e) {
    clientLogger(e.message);
    return {
      error: e.message,
    };
  }
};

export const buildDashUrl = () => {
  const { DASHBOARD_API_URL } = getConfigObject();
  return DASHBOARD_API_URL;
};

export const get = async (url, base = buildDashUrl()) =>
  await makeRequest(`${base}${url}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      Pragma: 'no-cache',
    },
  });

const remove = async (url, base = buildDashUrl()) =>
  await makeRequest(`${base}${url}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      Pragma: 'no-cache',
    },
  });

export const post = async (url, data, base = buildDashUrl()) =>
  await makeRequest(`${base}${url}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Pragma: 'no-cache',
    },
    body: JSON.stringify(data),
  });

const put = async (url, data, base = buildDashUrl()) =>
  await makeRequest(`${base}${url}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Pragma: 'no-cache',
    },
    body: JSON.stringify(data),
  });

// ************** COURSE *********************************
export const getCourseAbstract = (courseId) => {
  return get(`${VERSION1}/dashboards/courses/${courseId}/abstract`);
};

// ************** QUANT / QUAL *********************************
export const getDashboardsWidgets = (courseId) =>
  get(`${VERSION1}/dashboards/courses/${courseId}/dashboards-widgets`);

export const getWidgetLibrary = (courseId) =>
  get(`${VERSION1}/dashboards/courses/${courseId}/widgets`);

export const getSRLSurveys = (courseId) =>
  get(`${VERSION1}/survey/courses/${courseId}`);

export const getSRLSurveyResults = (courseId, assignmentId) =>
  get(`${VERSION1}/survey/courses/${courseId}/assignments/${assignmentId}`);

export const getSRLSurveyTableData = (courseId, assignmentId, cardId) =>
  get(
    `${VERSION2}/survey/courses/${courseId}/assignments/${assignmentId}/cards/${cardId}/table`
  );

export const getSRLSurveyResultsByCard = (courseId, assignmentId, cardId) =>
  get(
    `${VERSION2}/survey/courses/${courseId}/assignments/${assignmentId}/cards/${cardId}`
  );

export const getComponentAiSummary = (courseId, assignmentId, componentId) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/assignments/${assignmentId}/components/${componentId}/ai-summary`
  );

export const generateComponentAiSummary = (
  courseId,
  assignmentId,
  componentId
) =>
  post(
    `${VERSION1}/dashboards/courses/${courseId}/assignments/${assignmentId}/components/${componentId}/generate-ai-summary`,
    {}
  );

export const generateAssignmentAiSummaries = (courseId, assignmentId) =>
  post(
    `${VERSION1}/dashboards/courses/${courseId}/assignments/${assignmentId}/generate-ai-summaries`,
    {}
  );

export const addWidgetToDashboard = (
  courseId,
  dashboardId,
  widgetId,
  coordinates
) =>
  post(`${VERSION1}/dashboards/courses/${courseId}/dashboards-widgets`, {
    dashboardId,
    widgetId,
    coordinates,
  });

export const removeWidgetFromDashboard = (courseId, dashboardWidgetId) =>
  remove(
    `${VERSION1}/dashboards/courses/${courseId}/dashboards-widgets/${dashboardWidgetId}`
  );

export const updateWidgets = (courseId, widgets) =>
  put(`${VERSION1}/dashboards/courses/${courseId}/dashboards-widgets`, {
    widgets,
  });

export const getActiveUnitLOInsights = (
  courseId,
  dashboardWidgetId,
  queryParams
) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/widgets/${dashboardWidgetId}/active-unit-lo-insights${
      queryParams ? `?${queryParams}` : ''
    }`
  );

export const getCourseLoginData = (courseId, queryParams) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/login-report${
      queryParams ? `?${queryParams}` : ''
    }`
  );

export const getTimeScoreData = (courseId, toolName, assignmentId) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/tools/${toolName}/assignments/${assignmentId}/timeontask`
  );

export const getAssignmentInsights = (
  courseId,
  dashboardWidgetId,
  queryParams
) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/widgets/${dashboardWidgetId}/assignment-insights${
      queryParams ? `?${queryParams}` : ''
    }`
  );

export const getAssignmentsScoreInsights = (courseId, queryParams) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/assignment-score-snapshot${
      queryParams ? `?${queryParams}` : ''
    }`
  );

export const getAssignmentPerformanceData = (
  courseId,
  assignmentId,
  queryParams
) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/${assignmentId}/ngaperformance?${queryParams}`
  );

export const getUnitReport = (courseId, unitId) =>
  get(`${VERSION1}/dashboards/courses/${courseId}/units/${unitId}/reports`);

export const getOverviewReport = (courseId) =>
  get(`${VERSION2}/dashboards/courses/${courseId}/overview-reports`);

export const getInsightWidgets = (courseId) =>
  get(`${VERSION1}/dashboards/courses/${courseId}/insight-widgets`);

export const getSubunitReport = (courseId, subunitId) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/subunits/${subunitId}/reports`
  );

export const getUnitList = (courseId) =>
  get(`${VERSION1}/dashboards/courses/${courseId}/units`);

export const getSubunitList = (courseId, includeSubUnits) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/units${
      includeSubUnits ? '?includeSubunits=true' : ''
    }`
  );

export const getStudentRevisionPlanOverview = (courseId) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/student-revision-plan-reports`
  );

export const getRevisionPlansNotStartedReport = (courseId) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/revision-plan-not-started-reports`
  );

export const getStudentUnitReport = (courseId, unitId, studentId) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/units/${unitId}/students/${studentId}/reports`
  );

export const getStudentSubunitReport = (courseId, subunitId, studentId) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/subunits/${subunitId}/students/${studentId}/reports`
  );

export const getStudentOverviewReports = (courseId, studentId) =>
  get(
    `${VERSION2}/dashboards/courses/${courseId}/students/${studentId}/overview-reports`
  );

export const getDataNGAPerformance = (courseId, assignmentId) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/${assignmentId}/ngaperformance`
  );

export const getDataNGASummary = (courseId, assignmentId) =>
  get(`${VERSION1}/dashboards/courses/${courseId}/${assignmentId}/ngasummary`);

// *********************** SKILLS ******************************
export const getReflectionSummaryReport = (courseId) =>
  get(`${VERSION1}/dashboards/courses/${courseId}/reflection-summary-reports`);

export const getSkillsOverviewWidget = (courseId) =>
  get(`${VERSION1}/dashboards/courses/${courseId}/skills-overview-widgets`);

export const getFeedbackSummaryReport = (courseId) =>
  get(`${VERSION1}/dashboards/courses/${courseId}/feedback-summary-reports`);

export const getNotReviewedFeedbackSummaryReport = (courseId) =>
  get(
    `${VERSION1}/dashboards/courses/${courseId}/not-reviewed-feedback-summary-reports`
  );

export const getSkillsOverviewReport = (courseId) =>
  get(`${VERSION1}/dashboards/courses/${courseId}/skills-overview-reports`);

export const getSkillsNavigationData = (courseId) =>
  get(`${VERSION1}/dashboards/courses/${courseId}/skills-navigation`);

// *********************** ANALYTICS ******************************

export const sendAnalytics = (eventData) => {
  const encodedParams = Object.entries(eventData)
    .filter((entry) => !!entry[1])
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join('&');

  const analyticsUrl = `${
    getConfigObject().DASHBOARDS_ANALYTICS_URL
  }?${encodedParams}`;
  return makeRequest(analyticsUrl, {
    method: 'GET',
    mode: 'no-cors',
    headers: {
      Accept: 'application/json',
      Pragma: 'no-cache',
    },
  });
};
